<template>
    <ModalDialogContainer ref="modalContainer" v-model:display="displayStatus" :z-index="zIndex">
        <div
            class="modal-dialog modal-md modal-box-container"
            :class="`modal-box-${currentStep.modalType || modalType}`"
            role="document"
            :style="{ maxWidth: (currentStep.maxWidth || maxWidth) + 'px' }"
            @click="stopPropagationEvent"
        >
            <div class="modal-action-container">
                <div v-if="!currentStep.onlyComponent" class="modal-header" :class="currentStep.positionIconHeader || positionIconHeader">
                    <div class="icon-container">
                        <span v-if="(currentStep.modalType || modalType) === 'danger'" class="icon-spot">
                            <i class="fas fa-exclamation-triangle danger-icon"></i>
                        </span>
                        <span v-else-if="(currentStep.modalType || modalType) === 'info'" class="icon-spot">
                            <i class="fas fa-info-circle info-icon"></i>
                        </span>
                        <span v-else class="icon-spot">
                            <i class="fas fa-check-circle success-icon"></i>
                        </span>
                    </div>
                    <div class="modal-header-content">
                        <div v-if="currentStep.title || title" class="modal-title">{{ currentStep.title || title }}</div>
                        <p v-if="currentStep.message || message" class="text-message-color">
                            <RichText :text="currentStep.message || message" />
                        </p>
                    </div>
                </div>

                <AlertBox
                    v-if="!currentStep.onlyComponent && (currentStep.alertType || alertType) !== 'none'"
                    :class="currentStep.alertPosition || alertPosition"
                    :alert-type="currentStep.alertType || alertType"
                    :alert-message="currentStep.alertMessage || alertMessage"
                />

                <div class="modal-body">
                    <component
                        :key="currentStepIndex"
                        v-if="currentStep.component || customComponent"
                        :is="currentStep.component || customComponent"
                        v-bind="mergedComponentProps"
                        @close-modal="close"
                        @next-step="nextStep"
                        @prev-step="prevStep"
                        @go-to-step="goToStep"
                        @update-shared-data="updateSharedData"
                        @confirm="confirm"
                    ></component>
                </div>

                <div v-if="currentStep.displayFooter ?? displayFooter" class="modal-footer">
                    <span v-if="!(currentStep.displayCloseButton ?? displayCloseButton) && !currentStep.onlyComponent" class="modal-footer">
                        <button v-if="canGoBack" type="button" class="btn btn-secondary" @click="prevStep()">
                            <i class="fas fa-arrow-left"></i>
                            {{ $t("Back") }}
                        </button>
                        <button type="button" class="btn btn-secondary" @click="close">
                            {{ $t("Cancel") }}
                        </button>
                        <button type="button" class="btn btn-mr" :class="`btn-${currentStep.modalType || modalType}`" @click="confirm">
                            <i :class="currentStep.iconButton || iconButton"></i>
                            {{ currentStep.messageButton || messageButton || $t("Confirm") }}
                        </button>
                    </span>
                    <button v-else type="button" class="btn btn-secondary" @click="close">
                        {{ $t("Close") }}
                    </button>
                </div>
            </div>
        </div>
    </ModalDialogContainer>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { useVModel } from "../../utils/v-model";
import { getUniqueStringId } from "@/utils/unique-id";
import { Request } from "@asanrom/request-browser";
import AlertBox from "@/components/ui/AlertBox.vue";
import RichText from "@/components/ui/RichText.vue";
import type { ActionModalStep } from "@/types/action-modal";

export default defineComponent({
    components: {
        AlertBox,
        RichText,
    },
    name: "ActionModal",
    emits: ["update:display", "confirm"],
    props: {
        display: Boolean,
        onlyComponent: {
            type: Boolean,
            default: false,
        },
        modalType: {
            type: String as PropType<"danger" | "info" | "success">,
            default: "danger",
        },
        title: String,
        message: String,
        messageButton: String,
        iconButton: {
            type: String,
            default: "fas fa-check",
        },
        alertType: {
            type: String as PropType<"alert-info" | "alert-success" | "alert-danger" | "alert-warning" | "none">,
            default: "none",
        },
        alertPosition: {
            type: String as PropType<"top" | "middle" | "bottom">,
            default: "top",
        },
        alertMessage: {
            type: String,
            default: "Are you sure you want to proceed? This action cannot be undone.",
        },
        customComponent: {
            type: Object,
            default: null,
        },
        componentProps: {
            type: Object,
            default: () => ({}),
        },
        positionIconHeader: {
            type: String as PropType<"left" | "center" | "right" | "none">,
            default: "left",
        },
        displayFooter: {
            type: Boolean,
            default: true,
        },
        displayCloseButton: {
            type: Boolean,
            default: false,
        },
        steps: {
            type: Array as PropType<ActionModalStep[]>,
            default: () => [],
        },
        maxWidth: {
            type: Number as PropType<number | undefined>,
            default: 520,
        },
        zIndex: {
            type: Number as PropType<number | undefined>,
            default: undefined,
        },
    },

    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
            deleteRequestId: getUniqueStringId(),
        };
    },

    data() {
        return {
            busy: false,
            confirmation: "",
            currentStepIndex: 0,
            stepHistory: [] as number[],
            sharedStepData: {} as Record<string, any>,
        };
    },

    computed: {
        currentStep(): ActionModalStep {
            if (this.steps.length > 0) {
                return this.steps[this.currentStepIndex];
            }
            return {
                title: this.title,
                message: this.message,
                modalType: this.modalType,
                component: this.customComponent,
                componentProps: this.componentProps,
                alertType: this.alertType,
                alertPosition: this.alertPosition,
                alertMessage: this.alertMessage,
                positionIconHeader: this.positionIconHeader,
                displayFooter: this.displayFooter,
                displayCloseButton: this.displayCloseButton,
                onlyComponent: this.onlyComponent,
                messageButton: this.messageButton,
                iconButton: this.iconButton,
                maxWidth: this.maxWidth,
            };
        },

        canGoBack(): boolean {
            return this.stepHistory.length > 0;
        },

        availableStepIds(): string[] {
            return this.steps.map((step, index) => step.id || `step-${index}`).filter((id) => id !== this.currentStepId);
        },

        currentStepId(): string {
            return this.currentStep.id || `step-${this.currentStepIndex}`;
        },

        mergedComponentProps(): any {
            return {
                ...(this.currentStep.componentProps || this.componentProps),
                stepNavigation: {
                    currentStepId: this.currentStepId,
                    currentStepIndex: this.currentStepIndex,
                    totalSteps: this.steps.length,
                    availableStepIds: this.availableStepIds,
                    canGoBack: this.canGoBack,
                    canGoNext: this.currentStepIndex < this.steps.length - 1,
                },
                sharedData: this.sharedStepData,
            };
        },
    },
    methods: {
        close: function () {
            this.currentStepIndex = 0;
            this.stepHistory = [];
            this.sharedStepData = {};
            this.$closeModal();
        },

        updateSharedData: function (data: Record<string, any>) {
            this.sharedStepData = { ...this.sharedStepData, ...data };
        },

        nextStep: function () {
            if (this.currentStepIndex < this.steps.length - 1) {
                this.stepHistory.push(this.currentStepIndex);
                this.currentStepIndex++;
            }
        },

        prevStep: function (stepIdOrIndex?: string | number) {
            if (stepIdOrIndex !== undefined) {
                this.navigateToStep(stepIdOrIndex);
                return;
            }
            if (this.stepHistory.length > 0) {
                this.currentStepIndex = this.stepHistory.pop()!;
            }
        },

        goToStep: function (stepIdOrIndex: string | number) {
            this.stepHistory.push(this.currentStepIndex);
            this.navigateToStep(stepIdOrIndex);
        },

        navigateToStep: function (stepIdOrIndex: string | number) {
            if (typeof stepIdOrIndex === "number") {
                if (stepIdOrIndex >= 0 && stepIdOrIndex < this.steps.length) {
                    this.currentStepIndex = stepIdOrIndex;
                } else {
                    console.warn(`[ActionModal] Invalid step index: ${stepIdOrIndex}`);
                }
            } else {
                const index = this.steps.findIndex((step, i) => (step.id || `step-${i}`) === stepIdOrIndex);
                if (index !== -1) {
                    this.currentStepIndex = index;
                } else {
                    console.warn(`[ActionModal] Step not found: ${stepIdOrIndex}`);
                }
            }
        },

        escapeToClose: function (event) {
            if (event.key === "Escape") {
                this.close();
            }
        },

        stopPropagationEvent: function (e) {
            e.stopPropagation();
        },

        confirm: function () {
            const data = { ...this.sharedStepData };
            this.close();
            this.$emit("confirm", data);
        },
    },
    mounted: function () {
        if (this.display) {
            this.$autoFocus();
        }
    },
    beforeUnmount: function () {
        Request.Abort(this.deleteRequestId);
    },
    watch: {
        display(newVal) {
            if (newVal) {
                this.currentStepIndex = 0;
                this.stepHistory = [];
                this.$autoFocus();
            }
        },
    },
});
</script>

<style scoped>
@import "@/style/components/action-modal.css";
</style>
